import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useClientIndustryApi custom hook for client industry API operations.
 * - Provides CRUD operations for client industries using Apollo Client and GraphQL.
 * - Handles cache updates for client industry queries and mutations.
 * - Exposes helpers for reading/writing/updating client industries in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting client industries.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const CLIENT_INDUSTRY_QUERIES = {
  // Query to fetch all client industries
  GET_CLIENT_INDUSTRIES: gql`
    query GetClientIndustries {
      GetClientIndustries {
        client_industry_id
        industry_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Query to fetch a client industry by ID
  GET_CLIENT_INDUSTRY_BY_ID: gql`
    query GetClientIndustryById {
      GetClientIndustryById {
        client_industry_id
        industry_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to add a new client industry
  ADD_CLIENT_INDUSTRY: gql`
    mutation AddClientIndustry($industryName: String!) {
      AddClientIndustry(industry_name: $industryName) {
        client_industry_id
        industry_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing client industry
  UPDATE_CLIENT_INDUSTRY: gql`
    mutation UpdateClientIndustry(
      $clientIndustryId: ID!
      $industryName: String
    ) {
      UpdateClientIndustry(
        client_industry_id: $clientIndustryId
        industry_name: $industryName
      ) {
        client_industry_id
        industry_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a client industry
  DELETE_CLIENT_INDUSTRY: gql`
    mutation DeleteClientIndustry($clientIndustryId: ID!) {
      DeleteClientIndustry(client_industry_id: $clientIndustryId) {
        client_industry_id
        industry_name
        is_active
        created_on
        updated_on
      }
    }
  `,
};

/**
 * Cache operations for client industries.
 * - readClientIndustries: Reads client industries from Apollo cache.
 * - writeClientIndustries: Writes client industries to Apollo cache.
 * - updateClientIndustryInCache: Updates a client industry in Apollo cache.
 */
const cacheOperations = {
  readClientIndustries: () => {
    try {
      const { GetClientIndustries = [] } =
        client.readQuery({
          query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
        }) || {};
      return GetClientIndustries;
    } catch {
      return [];
    }
  },
  writeClientIndustries: (clientIndustries) => {
    client.writeQuery({
      query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
      data: { GetClientIndustries: clientIndustries },
    });
  },
  updateClientIndustryInCache: (updatedClientIndustry) => {
    const existingClientIndustries = cacheOperations.readClientIndustries();
    const updatedClientIndustries = existingClientIndustries.map(
      (clientIndustry) =>
        clientIndustry.client_industry_id ===
        updatedClientIndustry.client_industry_id
          ? updatedClientIndustry
          : clientIndustry
    );
    cacheOperations.writeClientIndustries(updatedClientIndustries);
  },
};

export const useClientIndustryApi = () => {
  // Mutation for adding a client industry
  const [addClientIndustryMutation] = useMutation(
    CLIENT_INDUSTRY_QUERIES.ADD_CLIENT_INDUSTRY,
    {
      update(cache, { data: { AddClientIndustry } }) {
        try {
          const existingData = cache.readQuery({
            query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
          });
          if (existingData) {
            cache.writeQuery({
              query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
              data: {
                GetClientIndustries: [
                  AddClientIndustry,
                  ...existingData.GetClientIndustries,
                ],
              },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after adding client industry :",
            error
          );
        }
      },
    }
  );

  // Mutation for updating a client industry
  const [updateClientIndustryMutation] = useMutation(
    CLIENT_INDUSTRY_QUERIES.UPDATE_CLIENT_INDUSTRY,
    {
      update(cache, { data: { UpdateClientIndustry } }) {
        try {
          const existingData = cache.readQuery({
            query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
          });
          if (existingData) {
            const newData = existingData.GetClientIndustries.map(
              (clientIndustry) =>
                clientIndustry.client_industry_id ===
                UpdateClientIndustry.client_industry_id
                  ? UpdateClientIndustry
                  : clientIndustry
            );
            cache.writeQuery({
              query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
              data: { GetClientIndustries: newData },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after updating client industry:",
            error
          );
        }
      },
    }
  );

  // Mutation for deleting a client industry
  const [deleteClientIndustryMutation] = useMutation(
    CLIENT_INDUSTRY_QUERIES.DELETE_CLIENT_INDUSTRY,
    {
      update(cache, { data: { DeleteClientIndustry } }) {
        try {
          const existingData = cache.readQuery({
            query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
          });
          if (existingData) {
            cache.writeQuery({
              query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
              data: {
                GetClientIndustries: existingData.GetClientIndustries.filter(
                  (clientIndustry) =>
                    clientIndustry.client_industry_id !==
                    DeleteClientIndustry.client_industry_id
                ),
              },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after deleting client industry:",
            error
          );
        }
      },
    }
  );

  /**
   * Fetches all client industries from the backend and updates the cache.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getClientIndustries = async () => {
    try {
      console.log("Calling getClientIndustries API...");
      const { data } = await client.query({
        query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });
      console.log("getClientIndustries response:", data);

      if (!data || !data.GetClientIndustries) {
        throw new Error("No data received from getClientIndustries");
      }

      // Update cache
      client.writeQuery({
        query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRIES,
        data: { GetClientIndustries: data.GetClientIndustries },
      });
      return { success: true, data: data.GetClientIndustries };
    } catch (error) {
      console.error("Error in getClientIndustries:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a client industry by its ID.
   * @param {string|number} client_industry_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getClientIndustryById = async (client_industry_id) => {
    try {
      const { data } = await client.query({
        query: CLIENT_INDUSTRY_QUERIES.GET_CLIENT_INDUSTRY_BY_ID,
        variables: { clientIndustryId: client_industry_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetClientIndustryById };
    } catch (error) {
      console.error("Error fetching client industry:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new client industry.
   * @param {Object} formData - { industry_name: string }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createClientIndustry = async (formData) => {
    try {
      const { data } = await addClientIndustryMutation({
        variables: {
          industryName: formData.industry_name,
        },
      });
      // Return the new client industry directly from mutation response
      return { success: true, data: data.AddClientIndustry };
    } catch (error) {
      console.error("Error creating client indsutry:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates an existing client industry.
   * @param {string|number} clientIndustryId
   * @param {Object} formData - { industry_name: string }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateClientIndustry = async (clientIndustryId, formData) => {
    try {
      const { data } = await updateClientIndustryMutation({
        variables: {
          clientIndustryId: clientIndustryId,
          industryName: formData.industry_name,
        },
      });
      // Return the updated client industry directly from mutation response
      return { success: true, data: data.UpdateClientIndustry };
    } catch (error) {
      console.error("Error updating client industry:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Deletes a client industry by its ID.
   * @param {string|number} client_industry_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteClientIndustry = async (client_industry_id) => {
    try {
      const { data } = await deleteClientIndustryMutation({
        variables: { clientIndustryId: client_industry_id },
      });

      return { success: true, data: data.DeleteClientIndustry };
    } catch (error) {
      console.error("Error deleting client industry:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getClientIndustries,
    getClientIndustryById,
    createClientIndustry,
    updateClientIndustry,
    deleteClientIndustry,
    cacheOperations,
  };
};
